import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Form, Button, ListGroup, FormControl, Modal, DropdownButton, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPencil,
    faUserCircle,
    faUserPlus,
    faCircleExclamation,
    faCheckCircle,
    faTimesCircle,
    faCopy,
    faMagnifyingGlass
} from '@fortawesome/free-solid-svg-icons';
import { getAgreementWithLowestStatus } from '../../../helpers/getAgreementWithLowestStatus';
import Layout from '../../../layout/Layout';
import { getInitialLoginData, registerUser } from '../../../redux/slices/userSlice';
import { fetchAdvertisers, resetAdvertisers } from '../../../redux/slices/adPortal/advertiserAdminSlice';
import EditEmail from './EditEmail';
import PasswordResetModal from './PasswordResetModal';
import ResendEmail from './ResendStatusEmail';
import '../order-tracker.scss';
import './admin.scss';

const Admin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [email, setEmail] = useState('');
    const [advertiserCode, setAdvertiserCode] = useState('');
    const [filter, setFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [showResendEmailModal, setShowResendEmailModal] = useState(false);
    const [currentAccount, setCurrentAccount] = useState({});
    const [message, setMessage] = useState('');
    const [modalTitle, setModalTitle] = useState('');

    const { advertisers, loading: advertisersLoading } = useSelector((state) => state.advertiserAdmin);
    const { initialLoginData, loading: userLoading } = useSelector((state) => state.user);

    const advertiserAccount = initialLoginData?.Account;

    const addSpacesBetweenCasing = (str) => {
        return str.replace(/([A-Z])/g, ' $1').trim();
    };

    const handleClearSearch = () => {
        setFilter('');
        dispatch(resetAdvertisers());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const userDetails = { Email: email, AdvertiserCode: advertiserCode };

        try {
            const response = await dispatch(registerUser(userDetails));
            if (response.payload && response.payload.Token) {
                setMessage('Account created successfully!');
                setModalTitle('Success');
            } else if (response.payload) {
                setMessage(response.payload);
                setModalTitle('Error');
            }
            setShowModal(true);
        } catch (error) {
            setMessage('An error occurred during account creation.');
            setModalTitle('Error');
            setShowModal(true);
        }

        setEmail('');
        setAdvertiserCode('');
    };

    const handleSearchHistory = () => {
        dispatch(fetchAdvertisers(filter));
    };

    const handleOpenEditModal = (account) => {
        setCurrentAccount(account);
        setShowEditModal(true);
    };

    const handleUpdateSuccess = (updatedAccount) => {
        setShowEditModal(false);
        setCurrentAccount({});
    };

    const handleViewAs = async (account) => {
        const userData = {
            UserID: account.UserID,
            RoleIds: [parseInt(account.RoleID, 10)],
        };

        const response = await dispatch(getInitialLoginData(userData));

        if (response.payload) {
            const initialLoginData = response.payload;
            const agreement = getAgreementWithLowestStatus(initialLoginData.Agreements);
            const agreementID = agreement?.AgreementID;
            navigate(`/order-status/${agreementID}`, { state: { initialLoginData } });
        }
    };

    const handleSendPasswordReset = (account) => {
        setCurrentAccount(account);
        setShowPasswordResetModal(true);
    };

    const handleResendEmail = (account) => {
        setCurrentAccount(account);
        setShowResendEmailModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleClosePasswordResetModal = () => {
        setShowPasswordResetModal(false);
        setCurrentAccount({});
    };

    const handleCloseResendEmailModal = () => {
        setShowResendEmailModal(false);
        setCurrentAccount({});
    };

    return (
        <Layout>
            <Card className="my-3">
                <Card.Header>
                    <FontAwesomeIcon
                        className="me-2"
                        icon={faUserPlus}
                    /> Create or Attach Customer Code to Existing Account
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group
                            className="mb-3"
                            controlId="formBasicEmail"
                        >
                            <Form.Label>Email address</Form.Label>
                            <Form.Control
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email"
                                required
                                type="email"
                                value={email}
                            />
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="formBasicAdvertiserCode"
                        >
                            <Form.Label>Customer Code</Form.Label>
                            <Form.Control
                                onChange={(e) => setAdvertiserCode(e.target.value)}
                                placeholder="ex. ABC123-US"
                                required
                                type="text"
                                value={advertiserCode}
                            />
                        </Form.Group>

                        <Button
                            className="ad-submit-btn"
                            type="submit"
                            variant="primary"
                        >Submit</Button>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="mt-3 history-card">
                <Card.Header>
                    <div className="d-flex justify-content-start align-items-center">
                        <div>
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faMagnifyingGlass}
                            /> Search Advertiser Accounts History
                        </div>
                    </div>
                    <FormControl
                        className="mt-2"
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Email or Customer Code"
                        type="text"
                        value={filter}
                    />
                    <Button
                        className="mt-2 ad-submit-btn"
                        onClick={handleSearchHistory}
                        variant="primary"
                    >Search</Button>
                    {advertisers.length > 0 && <Button
                        className="mt-2 ad-clear-btn"
                        onClick={handleClearSearch}
                    >Clear
                        <FontAwesomeIcon className="clear-btn-icon" icon={faTimesCircle} />
                    </Button>}
                </Card.Header>
                {(advertisersLoading || advertisers.length > 0) && <div class="adv-container">
                    {(!advertisersLoading && advertisers.length === 200) &&
                        <div className="accounts-found-msg">
                        <FontAwesomeIcon icon={faUserCircle} /> 200+ Accounts Found: </div>
                        }
                    {(!advertisersLoading && (advertisers.length > 0 && advertisers.length < 200)) &&
                        <div className="accounts-found-msg">
                            <FontAwesomeIcon icon={faUserCircle} /> {advertisers.length} Accounts Found:
                        </div>}
                    <div className="adv-scroll-list">
                        <ListGroup variant="flush">
                            {advertisersLoading ? (
                                <ListGroup.Item className="loading-msg">Loading...</ListGroup.Item>
                            ) : (
                                advertisers.map((item, index) => (
                                    <React.Fragment key={index}>
                                        <ListGroup.Item className="list-grp-item">
                                            <span className="list-grp-row">
                                                <FontAwesomeIcon className="list-grp-account-icon" icon={faUserCircle} />
                                                <span className="list-grp-col">
                                                    <div>Email: {item.Email}
                                                        <FontAwesomeIcon
                                                            className="me-2 text-secondary ms-2 edit-li-item-icon"
                                                            icon={faPencil}
                                                            onClick={() => handleOpenEditModal(item)}
                                                        />
                                                    </div>
                                                    <div>User ID: {item.UserID}
                                                        <FontAwesomeIcon
                                                            className="me-2 text-secondary ms-2 edit-li-item-icon"
                                                            title="Copy to Clipboard"
                                                            icon={faCopy}
                                                            onClick={() => { navigator.clipboard.writeText(item.UserID) }}
                                                        /></div>
                                                    <div>Customer Code: {item.AdvertiserCode}
                                                        <FontAwesomeIcon
                                                            className="me-2 text-secondary ms-2 edit-li-item-icon"
                                                            title="Copy to Clipboard"
                                                            icon={faCopy}
                                                            onClick={() => { navigator.clipboard.writeText(item.AdvertiserCode) }}
                                                        /></div>
                                                </span>
                                                <span className="list-grp-col grp-item-info-spacing">
                                                    <div>Created On: {new Date(item.CreatedOn).toLocaleDateString()}</div>
                                                    <div>Password Created:
                                                        {item.PasswordCreated ? (
                                                            <FontAwesomeIcon
                                                                className="text-success ms-2"
                                                                icon={faCheckCircle}
                                                            />
                                                        ) : (
                                                            <FontAwesomeIcon
                                                                className="text-danger ms-2"
                                                                icon={faTimesCircle}
                                                            />
                                                        )}
                                                    </div>
                                                    <div>Current Status: {addSpacesBetweenCasing(item.AdStatus)}</div>
                                                </span>
                                                <span className="list-grp-col grp-item-info-spacing">
                                                    <DropdownButton
                                                        className="mt-2 actions-btn"
                                                        id="dropdown-basic-button"
                                                        title="Actions"
                                                        variant="outline-success"
                                                    >
                                                        <span className="actions-btn-dropdown-item">
                                                            <Dropdown.Item
                                                                onClick={() => handleViewAs(item)}>
                                                                View as Customer
                                                            </Dropdown.Item>
                                                        </span>
                                                        <span className="actions-btn-dropdown-item">
                                                            <Dropdown.Item
                                                                onClick={() => handleSendPasswordReset(item)}>
                                                                Send Password Reset
                                                            </Dropdown.Item>
                                                        </span>
                                                        <span className="actions-btn-dropdown-item">
                                                            <Dropdown.Item
                                                                onClick={() => handleResendEmail(item)}>
                                                                Resend Welcome Email
                                                            </Dropdown.Item>
                                                        </span>
                                                    </DropdownButton>
                                                </span>
                                            </span>
                                        </ListGroup.Item>
                                    </React.Fragment>
                                ))
                            )}
                        </ListGroup>
                    </div>
                </div>}
            </Card>

            <EditEmail
                currentAccount={currentAccount}
                handleClose={() => setShowEditModal(false)}
                onUpdateSuccess={handleUpdateSuccess}
                show={showEditModal}
            />

            <PasswordResetModal
                handleClose={handleClosePasswordResetModal}
                initialEmail={currentAccount.Email}
                show={showPasswordResetModal}
            />

            <ResendEmail
                handleClose={handleCloseResendEmailModal}
                initialAdvertiserCode={currentAccount.AdvertiserCode}
                show={showResendEmailModal}
            />

            <Modal
                centered
                onHide={handleCloseModal}
                show={showModal}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon
                            className="text-white"
                            icon={faCircleExclamation}
                            size="2xl"
                        /> {modalTitle}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {message}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={handleCloseModal}
                        variant="secondary"
                    >Close</Button>
                </Modal.Footer>
            </Modal>
        </Layout>
    );
};

export default Admin;
